import { inject, Injectable } from '@angular/core'

import { firstValueFrom, Observable, tap } from 'rxjs'

import { DataQueryProps, Role, UserProfile, UserProfileTiView, UserRole } from '@ti/contracts'
import { DataGridColumnActionsFn, DataGridColumnType } from '@ti/core/contracts/data-grid'
import { ListModelConfig } from '@ti/core/models/types'
import { BaseUsersModel, UserGridData } from '@ti/shared/user'

import { TiTspUsersStore } from '../../common/stores'

@Injectable()
export class AdminTspUsersModel extends BaseUsersModel {
  protected readonly modelName = 'TSPUsersModel'
  protected readonly usersStore = inject(TiTspUsersStore)

  protected loadPage(props: DataQueryProps): Observable<UserProfile[]> {
    return this.api.ti.user
      .listTsp$(props)
      .pipe(tap((users) => this.usersStore.merge(undefined, users)))
  }

  protected override mapUserData(
    user: UserProfileTiView,
    roles: Role[],
    roleLabels: Record<UserRole, string>,
  ) {
    return {
      user: user,
      role: this.rolesManager.isCustom(user.roleId)
        ? roles.find((role) => role.id === user.roleId)?.name
        : roleLabels[user.roleId],
      lastActivity: user.inviteAcceptedAt
        ? {
            type: 'login',
            date: user.inviteAcceptedAt,
          }
        : {
            type: 'invite',
            date: user.invitedAt as Date,
          },
      mfa: user.mfaStatus?.length,
      tspName: user.tspName ?? '---',
      auditId: user.auditId ?? undefined,
    }
  }

  protected countAllUsers() {
    const options = { search: '' }
    return firstValueFrom(this.api.ti.user.countTsp$(options))
  }

  protected override config(): ListModelConfig {
    return {
      name: this.modelName,
      retainCacheUrl: [''],
      defaultOrderColumn: 'favourite',
      defaultOrderDirection: 'ASC',
      gridColumns: [
        {
          field: 'favourite',
          label: 'users.grid.favourite',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'user',
          label: 'users.grid.user',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'role',
          label: 'users.grid.role',
          type: DataGridColumnType.Text,
          sortable: true,
        },
        {
          field: 'tspName',
          label: 'users.grid.tsp',
          type: DataGridColumnType.Text,
          sortable: true,
        },
        {
          field: 'lastActivity',
          // @TODO change label back when activity tracking will be implemented
          // label: 'users.grid.last-activity',
          label: 'users.grid.created-at',
          type: DataGridColumnType.Template,
          sortable: false,
        },
        {
          field: 'loginAs',
          label: '',
          type: DataGridColumnType.Template,
          sortable: false,
        },
        {
          field: 'actions',
          label: '',
          type: DataGridColumnType.Actions,
          actions: ((data: UserGridData) => this.getOptionsStream(data)) as DataGridColumnActionsFn,
        },
      ],
    }
  }
}

export const provideAdminTspUsersModel = () => {
  return [AdminTspUsersModel, { provide: BaseUsersModel, useExisting: AdminTspUsersModel }]
}
